import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ProjectFlowUnified, { DEMO_FLOW_STAGES } from "../components/ProjectFlowUnified";
import TaskGantt from "../components/TaskGantt";
import { Button } from "../components/ui/button";
import { ArrowLeft, Settings, BarChart3, GitBranch } from "lucide-react";
import { toast } from "sonner";
import api, { projectTimelineSnapshotApi, projectWeekStatusApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// PROJECTTIMELINE_GANTT_V2 — derives milestones from timeline_progress.stage_history
const GANTT_EDIT_ROLES = new Set(["admin", "pm_lead", "pm", "team", "art_director"]);

const ProjectTimelinePage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [stages, setStages] = useState([]);
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [useDemo, setUseDemo] = useState(false);

  const [view, setView] = useState("gantt"); // gantt | flow
  const [snapshot, setSnapshot] = useState([]);
  const [weekStatuses, setWeekStatuses] = useState([]);

  const isDemoMode = !projectId || projectId === "demo";
  const canEditWeekStatus = !!user && GANTT_EDIT_ROLES.has(user.role);

  const loadProjectData = useCallback(async () => {
    setLoading(true);
    try {
      if (isDemoMode) {
        setProject({ name: "360° Tour", project_number: "DEMO-001" });
        setStages(DEMO_FLOW_STAGES);
        setCurrentStageIndex(2);
        setUseDemo(true);
        setSnapshot([]);
        setWeekStatuses([]);
        return;
      }

      const projectRes = await api.get(`/projects/${projectId}`);
      if (projectRes.data) {
        setProject(projectRes.data);
        if (projectRes.data.timeline_stages?.length > 0) {
          setStages(projectRes.data.timeline_stages);
          setCurrentStageIndex(projectRes.data.current_stage_index || 0);
          setUseDemo(false);
        } else {
          setStages(DEMO_FLOW_STAGES);
          setCurrentStageIndex(2);
          setUseDemo(true);
        }
      }

      try {
        const snapRes = await projectTimelineSnapshotApi.get(projectId);
        setSnapshot(Array.isArray(snapRes.data) ? snapRes.data : []);
      } catch (err) {
        console.error("Failed to load timeline snapshot:", err);
        setSnapshot([]);
      }

      try {
        const wsRes = await projectWeekStatusApi.list(projectId);
        setWeekStatuses(wsRes.data || []);
      } catch (err) {
        console.error("Failed to load week statuses:", err);
        setWeekStatuses([]);
      }
    } catch (error) {
      console.error("Error loading project:", error);
      setProject({ name: "360° Tour", project_number: "DEMO-001" });
      setStages(DEMO_FLOW_STAGES);
      setCurrentStageIndex(2);
      setUseDemo(true);
    } finally {
      setLoading(false);
    }
  }, [projectId, isDemoMode]);

  useEffect(() => {
    loadProjectData();
  }, [loadProjectData]);

  const setWeekStatus = async (weekEnding, status) => {
    try {
      const res = await projectWeekStatusApi.upsert(projectId, { week_ending: weekEnding, status });
      setWeekStatuses((prev) => {
        const idx = prev.findIndex((w) => w.week_ending === weekEnding);
        if (idx === -1) return [...prev, res.data].sort((a, b) => (a.week_ending > b.week_ending ? 1 : -1));
        const copy = [...prev];
        copy[idx] = res.data;
        return copy;
      });
    } catch (err) {
      console.error("Set week status failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to update week status");
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      <header className="border-b border-zinc-800 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-zinc-400 hover:text-zinc-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-zinc-50">
                  {project?.name || "Project"} Timeline
                </h1>
                {project?.project_number && (
                  <p className="text-sm text-zinc-500">{project.project_number}</p>
                )}
              </div>
            </div>

            <div className="inline-flex rounded-md border border-zinc-700 overflow-hidden">
              <button
                type="button"
                onClick={() => setView("gantt")}
                className={
                  "px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors " +
                  (view === "gantt" ? "bg-zinc-700 text-zinc-50" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800")
                }
              >
                <BarChart3 className="h-3.5 w-3.5" /> Gantt
              </button>
              <button
                type="button"
                onClick={() => setView("flow")}
                className={
                  "px-3 py-1.5 text-xs flex items-center gap-1.5 transition-colors " +
                  (view === "flow" ? "bg-zinc-700 text-zinc-50" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800")
                }
              >
                <GitBranch className="h-3.5 w-3.5" /> Flow
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {useDemo && view === "flow" && (
          <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center gap-3">
            <Settings className="w-5 h-5 text-blue-400" />
            <p className="text-sm text-blue-300">
              <strong>Demo Mode:</strong> Showing sample 360° Tour workflow. Configure timeline templates in Settings to use with real projects.
            </p>
          </div>
        )}

        {view === "gantt" ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/30 p-4">
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div>
                  <h2 className="text-sm font-medium text-zinc-50">Task Gantt</h2>
                  <p className="text-[11px] text-zinc-500 mt-0.5 max-w-2xl">
                    One source of truth: each task's lane shows start → deadline, with colored flags marking every move to a stage of the task's own timeline. Stages and colors come from <a href="/settings/timelines" className="text-orange-400 hover:underline">/settings/timelines</a>. PMs flip the Friday badges at the bottom to log weekly health.
                  </p>
                </div>
              </div>
              {snapshot.length > 0 && snapshot.every((t) => !t.start_date) && (
                <div className="mb-3 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/5 text-[11px] text-amber-200">
                  <strong>Heads-up:</strong> none of this project's tasks have a <code className="text-amber-100">start_date</code> — bars collapse to 1-day markers on the deadline. Set Start Date on tasks for full bars.
                </div>
              )}
              <TaskGantt
                snapshot={snapshot}
                weekStatuses={weekStatuses}
                onSetWeekStatus={setWeekStatus}
                canEditWeekStatus={canEditWeekStatus && !isDemoMode}
              />
            </div>
          </div>
        ) : (
          <ProjectFlowUnified
            projectName={project?.name || "360° Tour"}
            startDate={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)}
            stages={stages}
            currentStageIndex={currentStageIndex}
            contractAmount={project?.contract_amount || 50000}
            delayRatePercent={project?.delay_rate_percent || 0.1}
          />
        )}
      </main>
    </div>
  );
};

export default ProjectTimelinePage;
